import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Box,
  CircularProgress,
} from "@mui/material";
import { Warning, Lan, SettingsEthernet } from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import PropTypes from "prop-types";

const ResetConfirmationDialog = ({ open, onClose, onConfirm, selectedSwitch, resetLoading }) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby="reset-individual-dialog-title"
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle id="reset-individual-dialog-title" sx={{ pb: 1 }}>
      <Box display="flex" alignItems="center">
        <Warning
          sx={{
            mr: 1.5,
            fontSize: "1.5rem",
            color: "#ed6c02",
          }}
        />
        <MDTypography variant="h6" fontWeight="bold" color="dark">
          Confirmar Reinicio de Puerto
        </MDTypography>
      </Box>
    </DialogTitle>

    <DialogContent>
      <MDBox sx={{ pt: 1 }}>
        {/* DISPOSITIVO SELECCIONADO */}
        <MDBox
          sx={{
            backgroundColor: "#e3f2fd",
            p: 2.5,
            borderRadius: "8px",
            mb: 3,
            border: "1px solid rgba(33, 150, 243, 0.3)",
          }}
        >
          <MDTypography variant="subtitle2" fontWeight="bold" color="info" gutterBottom>
            PUERTO SELECCIONADO:
          </MDTypography>

          <Box display="flex" alignItems="center" mb={1}>
            <SettingsEthernet
              sx={{
                mr: 1.5,
                color: "#1976d2",
              }}
            />
            <MDTypography variant="body1" fontWeight="medium">
              {selectedSwitch?.name || "Switch no seleccionado"}
            </MDTypography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={2} mt={1.5}>
            {selectedSwitch && (
              <>
                <Chip
                  label={`Switch: ${selectedSwitch.name}`}
                  size="small"
                  sx={{
                    color: "#1976d2",
                    borderColor: "#1976d2",
                    backgroundColor: "#e3f2fd",
                  }}
                  variant="outlined"
                />
                <Chip
                  label={`IP: ${selectedSwitch.ip || "Sin IP"}`}
                  size="small"
                  sx={{
                    color: "#0288d1",
                    borderColor: "#0288d1",
                    backgroundColor: "#e3f2fd",
                  }}
                  variant="outlined"
                />
                <Chip
                  label={`Puerto: ${selectedSwitch.puerto || "N/A"}`}
                  size="small"
                  sx={{
                    color: "#7b1fa2",
                    borderColor: "#7b1fa2",
                    backgroundColor: "#f3e5f5",
                  }}
                  variant="outlined"
                />
              </>
            )}
          </Box>
        </MDBox>

        <MDBox
          sx={{
            backgroundColor: "#fff3e0",
            borderLeft: "4px solid #ed6c02",
            p: 2,
            mb: 3,
            borderRadius: "4px",
          }}
        >
          <Box display="flex" alignItems="flex-start">
            <Warning
              sx={{
                color: "#ed6c02",
                mr: 1.5,
                mt: 0.25,
              }}
            />
            <MDTypography variant="body2" fontWeight="medium">
              <strong>IMPORTANTE:</strong> El reinicio interrumpirá temporalmente la conectividad de
              todos los dispositivos conectados a este puerto.
            </MDTypography>
          </Box>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant="body1" fontWeight="medium" color="primary" gutterBottom>
            ¿Confirmas el reinicio del puerto seleccionado?
          </MDTypography>
          <MDTypography variant="body2" color="secondary">
            El puerto se reiniciará y volverá a estar disponible en aproximadamente 1-2 minutos.
          </MDTypography>
        </MDBox>

        <MDBox
          sx={{
            backgroundColor: "#f5f5f5",
            p: 2,
            borderRadius: "6px",
            mb: 2,
          }}
        >
          <MDTypography variant="body2" color="secondary">
            • Asegúrate de que no haya operaciones críticas en curso
            <br />
            • Verifica que los usuarios estén informados
            <br />• El reinicio no afectará la configuración permanente
          </MDTypography>
        </MDBox>
      </MDBox>
    </DialogContent>

    <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
      <Box display="flex" justifyContent="space-between" width="100%">
        <MDButton
          onClick={onClose}
          variant="outlined"
          color="secondary"
          sx={{
            textTransform: "none",
            fontWeight: "medium",
            borderRadius: "8px",
            px: 3,
          }}
        >
          Cancelar
        </MDButton>

        <MDButton
          onClick={onConfirm}
          variant="gradient"
          color="warning"
          startIcon={resetLoading ? <CircularProgress size={16} /> : <Warning />}
          disabled={resetLoading || !selectedSwitch}
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            borderRadius: "8px",
            px: 4,
            boxShadow: "0 2px 4px rgba(237, 108, 2, 0.3)",
            "&:hover": {
              boxShadow: "0 4px 8px rgba(237, 108, 2, 0.4)",
            },
          }}
        >
          {resetLoading ? "PROCESANDO..." : "CONFIRMAR REINICIO"}
        </MDButton>
      </Box>
    </DialogActions>
  </Dialog>
);

ResetConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedSwitch: PropTypes.object,
  resetLoading: PropTypes.bool,
};

export default React.memo(ResetConfirmationDialog);
